import { Alert, Grid } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import {
  AttendanceByCompanyItem,
  AttendanceByPsychologistItem,
  AttendanceFilterOptions,
  AttendanceFilters,
  AttendanceLogbookItem,
  AttendanceStatus,
  AttendanceStatusDistributionItem,
  AttendanceSummary,
  AttendanceTimelineItem,
  getAttendanceByCompany,
  getAttendanceByPsychologist,
  getAttendanceFilterOptions,
  getAttendanceLogbook,
  getAttendanceSummary,
  getAttendanceTimeline,
  getStatusDistribution,
} from "src/apis/dashboardAttendance";
import PageContainer from "src/pages/components/PageContainer";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import { isUserAdministrator, isUserCompany } from "src/utils/auth";
import { getFirstDayOfMonth, getLastDayOfMonth } from "src/utils/date";
import AttendanceByCompanyChart from "./components/AttendanceByCompanyChart";
import AttendanceByPsychologistChart from "./components/AttendanceByPsychologistChart";
import AttendanceFiltersBar, {
  AttendanceFiltersState,
} from "./components/AttendanceFilters";
import AttendanceLogbookTable from "./components/AttendanceLogbookTable";
import AttendanceStatusDonut from "./components/AttendanceStatusDonut";
import AttendanceSummaryCards from "./components/AttendanceSummaryCards";
import AttendanceTimelineChart from "./components/AttendanceTimelineChart";

const buildDefaultState = (): AttendanceFiltersState => ({
  companyId: "",
  status: "",
  unit: "",
  sector: "",
  serviceType: "",
  startDate: getFirstDayOfMonth(),
  endDate: getLastDayOfMonth(),
});

const DashboardAttendance: FC = (): JSX.Element => {
  const isAdmin = isUserAdministrator();
  const canAccess = isUserCompany();

  const [state, setState] = useState<AttendanceFiltersState>(buildDefaultState);
  const [options, setOptions] = useState<AttendanceFilterOptions | null>(null);

  const [summary, setSummary] = useState<AttendanceSummary | null>(null);
  const [timeline, setTimeline] = useState<AttendanceTimelineItem[]>([]);
  const [distribution, setDistribution] = useState<
    AttendanceStatusDistributionItem[]
  >([]);
  const [byCompany, setByCompany] = useState<AttendanceByCompanyItem[]>([]);
  const [byPsychologist, setByPsychologist] = useState<
    AttendanceByPsychologistItem[]
  >([]);
  const [logbook, setLogbook] = useState<AttendanceLogbookItem[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const buildApiFilters = useCallback(
    (current: AttendanceFiltersState): AttendanceFilters => ({
      company_id: isAdmin && current.companyId ? current.companyId : undefined,
      status: (current.status as AttendanceStatus) || undefined,
      unit: current.unit || undefined,
      sector: current.sector || undefined,
      service_type: current.serviceType || undefined,
      start_date: current.startDate,
      end_date: current.endDate,
    }),
    [isAdmin]
  );

  const fetchData = useCallback(
    async (current: AttendanceFiltersState) => {
      setIsLoading(true);
      setError("");
      try {
        const apiFilters = buildApiFilters(current);
        const [
          summaryData,
          timelineData,
          distributionData,
          byCompanyData,
          byPsychologistData,
          logbookData,
        ] = await Promise.all([
          getAttendanceSummary(apiFilters),
          getAttendanceTimeline(apiFilters),
          getStatusDistribution(apiFilters),
          getAttendanceByCompany(apiFilters),
          getAttendanceByPsychologist(apiFilters),
          getAttendanceLogbook(apiFilters),
        ]);
        setSummary(summaryData);
        setTimeline(timelineData);
        setDistribution(distributionData);
        setByCompany(byCompanyData);
        setByPsychologist(byPsychologistData);
        setLogbook(logbookData);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível carregar a dashboard de atendimentos."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [buildApiFilters]
  );

  useEffect(() => {
    if (!canAccess) return;
    getAttendanceFilterOptions({})
      .then(setOptions)
      .catch(() => setOptions(null));
    fetchData(buildDefaultState());
  }, [canAccess, fetchData]);

  const handleChange = (patch: Partial<AttendanceFiltersState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  };

  const handleReset = () => {
    const next = buildDefaultState();
    setState(next);
    fetchData(next);
  };

  const isEmpty = summary !== null && summary.total === 0;

  return (
    <PageContainer
      title="Dashboard de Atendimentos"
      error={error}
      guard={() => canAccess}
    >
      <AttendanceFiltersBar
        isAdmin={isAdmin}
        options={options}
        state={state}
        onChange={handleChange}
        isLoading={isLoading}
        onSearch={() => fetchData(state)}
        onReset={handleReset}
      />

      {isLoading && <SkeletonLoad variant="cards" rows={3} />}

      {!isLoading && summary && (
        <>
          {isEmpty && (
            <Alert severity="info">
              Nenhum atendimento encontrado para os filtros selecionados.
            </Alert>
          )}

          <AttendanceSummaryCards summary={summary} />

          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <AttendanceTimelineChart data={timeline} />
            </Grid>
            <Grid item xs={12} lg={4}>
              <AttendanceStatusDonut data={distribution} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <AttendanceByCompanyChart data={byCompany} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <AttendanceByPsychologistChart data={byPsychologist} />
            </Grid>
          </Grid>

          <AttendanceLogbookTable data={logbook} />
        </>
      )}
    </PageContainer>
  );
};

export default DashboardAttendance;
